import React, {
  useCallback,
  createContext,
  useState,
  useMemo,
  useEffect,
} from 'react';

import {
  getModifierMinSelections,
  getActiveModifierOptions,
  optionsHasChildren,
  isMultiSelect,
  optionLinksChildModifierGroup,
} from './utils';

export const modifierContext = createContext();

export default function ModifierProvider({
  modifiers, children, selectedOptions: selectedOptionsProp, modifiersTree: modifiersTreeProp,
}) {
  const [stack, setStack] = useState([]);
  const [selectedModifiers, setSelectedModifiers] = useState({});
  const allOptions = useMemo(
    () => (modifiers || []).flatMap((m) => getActiveModifierOptions(m)),
    [modifiers],
  );
  const getModifier = useCallback((o) => modifiers.find((m) => m.id === o.modifier), [modifiers]);

  const getOptionKey = useCallback((o) => {
    const currentKey = `${o.modifier}-${o.id}`;
    const parentOption = allOptions.find((p) => optionLinksChildModifierGroup(p, o.modifier));
    if (!parentOption) {
      return currentKey;
    }
    return `${getOptionKey(parentOption)}/${currentKey}`;
  }, [allOptions]);

  const getModifierKey = useCallback((m) => {
    const currentKey = m.id;
    const parentOption = allOptions.find((op) => optionLinksChildModifierGroup(op, m.id));
    if (!parentOption) {
      return currentKey;
    }
    return `${getOptionKey(parentOption)}/${currentKey}`;
  }, [getOptionKey, allOptions]);

  // Initialize selectedModifiers from selectedOptionsProp (when editing) or default options
  useEffect(() => {
    if (selectedOptionsProp && selectedOptionsProp.length > 0 && allOptions.length > 0) {
      // When editing: restore previously selected modifiers
      const restoredModifiers = initializeFromSelectedOptions({
        selectedOptionsProp,
        allOptions,
        getModifier,
        getOptionKey,
        modifiersTree: modifiersTreeProp,
      });
      setSelectedModifiers(restoredModifiers);
    } else {
      // When adding new: use default options
      setSelectedModifiers(
        getSelectedOptions({ allOptions, getModifier })
          .reduce((acc, op) => ({ ...acc, [getOptionKey(op)]: { ...op, qty: op.default_qty || 1 } }), {}),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allOptions, getOptionKey, selectedOptionsProp, modifiersTreeProp]);

  const doesKeyMatchModifier = useCallback(
    ({ key, modifier }) => new RegExp(`${getModifierKey(modifier)}-[\\d]+$`).test(key),
    [getModifierKey],
  );

  const getTotalQty = useCallback(
    (modifier) => Object.entries(selectedModifiers)
      .filter(
        ([key, value]) => value && doesKeyMatchModifier({ key, modifier }),
      )
      .reduce((total, [_, { qty }]) => total + qty, 0),
    [doesKeyMatchModifier, selectedModifiers],
  );

  const getSelectedOption = useCallback(
    ({ option }) => selectedModifiers[getOptionKey(option)],
    [selectedModifiers, getOptionKey],
  );

  const hasSelectionsSatisfied = useCallback(
    (modifier) => {
      const minSelections = getModifierMinSelections(modifier);
      const totalQty = getTotalQty(modifier);
      return totalQty >= minSelections;
    },
    [getTotalQty],
  );

  const unSatisfiedModifiers = useMemo(() => {
    if (!modifiers || !modifiers.length) {
      return [];
    }
    const rootModifiers = modifiers.filter((m) => !m.options_parents.length);
    const selectedModifierWithNested = Object.entries(selectedModifiers).filter((m) => m[1].modifiers_children?.length);
    // Check ALL nested modifiers, not just the first one
    const mandatoryNestedModifierChildren = selectedModifierWithNested.flatMap((selected) => {
      const parentKey = selected[0];
      const parentOption = selected[1];
      if (!parentOption.modifiers_children?.length) return [];
      // Get all nested modifiers for this parent option
      return parentOption.modifiers_children
        .map((child) => {
          const nestedModifier =
            typeof child === 'object' && child !== null
              ? child
              : modifiers.find((m) => m.id === child);
          if (!nestedModifier) return null;
          // Check if this nested modifier is satisfied by counting only options under this parent
          const nestedModifierKey = getModifierKey(nestedModifier);
          // For nested modifiers, we need to check if options under this specific parent are selected
          // The nestedModifierKey already includes the parent path (e.g., "1-10/2")
          // So we check if any selected option key starts with that pattern
          const nestedOptionsSelected = Object.entries(selectedModifiers).filter(([key, value]) => {
            if (!value) return false;
            // Check if this key is for an option in the nested modifier (starts with nestedModifierKey + '-')
            return key.startsWith(`${nestedModifierKey}-`);
          });
          const totalQty = nestedOptionsSelected.reduce((sum, [_, opt]) => sum + (opt.qty || 1), 0);
          const minSelections = getModifierMinSelections(nestedModifier);
          // Return the modifier if it's not satisfied
          return totalQty < minSelections ? nestedModifier : null;
        })
        .filter(Boolean);
    });
    return [...rootModifiers, ...mandatoryNestedModifierChildren].filter((m) => !hasSelectionsSatisfied(m));
  }, [modifiers, hasSelectionsSatisfied, selectedModifiers, getModifierKey]);

  const pushIntoStack = useCallback((option) => {
    const modifier = getModifier(option);
    setStack((s) => ([...s, { modifier, option }]));
  }, [getModifier]);

  const handleChange = useCallback(
    ({ modifier, option, value }) => {
      const key = getOptionKey(option);

      if (value && optionsHasChildren(option)) {
        setStack((current) => [...current, { modifier, option }]);
      }

      let revalidateRequired = true;
      let selectedModifiersEntries = Object.entries({
        ...selectedModifiers,
        [key]: value,
      });

      while (revalidateRequired) {
        revalidateRequired = false;
        selectedModifiersEntries = selectedModifiersEntries.filter(
          ([currentOptionKey, currentOptionValue]) => {
            if (!currentOptionValue) {
              revalidateRequired = true;
              return false;
            }
            if (currentOptionKey === key) {
              return true;
            }

            // nested modifier
            if (currentOptionKey.split('/').length > 1) {
              const parentKey = currentOptionKey
                .split('/')
                .slice(0, -1)
                .join('/');
              // if parent has been removed, remove this as well
              if (!selectedModifiersEntries.some(([mk]) => mk === parentKey)) {
                revalidateRequired = true;
                return false;
              }
            }

            // remove other options for single-select option types
            if (
              !isMultiSelect(modifier) &&
              doesKeyMatchModifier({ key: currentOptionKey, modifier })
            ) {
              revalidateRequired = true;
              return false;
            }

            return true;
          },
        );
      }

      setSelectedModifiers(Object.fromEntries(selectedModifiersEntries));
    },
    [selectedModifiers, doesKeyMatchModifier, getOptionKey],
  );

  const isSelected = useCallback(
    ({ modifier, option }) => {
      const key = option ? getOptionKey(option) : getModifierKey(modifier);
      return Object.keys(selectedModifiers).some((k) => k.startsWith(key));
    },
    [getModifierKey, getOptionKey, selectedModifiers],
  );

  const getNormalizedSelectedModifiers = useCallback(() => {
    const keys = Object.keys(selectedModifiers);
    return Object.entries(selectedModifiers)
      // .filter(
      //   ([key]) => !keys.some((k) => k.length > key.length && k.includes(key)),
      // )
      .map(([_, value]) => value);
  }, [selectedModifiers]);

  const getSelectedModifiersTree = useCallback(() => {
    const keys = Object.keys(selectedModifiers);
    return Object.entries(selectedModifiers)
      .filter(
        ([key]) => !keys.some((k) => k.length > key.length && k.includes(key)),
      )
      .reduce(
        (acc, [key, { qty: quantity }]) => ({ ...acc, [key]: { quantity } }),
        {},
      );
  }, [selectedModifiers]);

  const getNestedOptions = useCallback(
    (optionOrId) => {
      const option =
        typeof optionOrId === 'object' && optionOrId !== null
          ? optionOrId
          : allOptions.find((o) => o.id === optionOrId);
      if (option?.modifiers_children?.length) {
        return option.modifiers_children
          .map((child) => (typeof child === 'object' && child !== null ? child : modifiers.find((m) => m.id === child)))
          .filter(Boolean);
      }
      const parentId =
        typeof optionOrId === 'object' && optionOrId !== null ? optionOrId.id : optionOrId;
      if (option == null && parentId == null) {
        return [];
      }
      return modifiers.filter((m) => m.options_parents.includes(parentId));
    },
    [modifiers, allOptions],
  );

  // Check if a parent option has selected nested options (for auto-expanding when editing)
  const hasSelectedNestedOptions = useCallback((option) => {
    if (!option?.modifiers_children?.length) return false;
    const optionKey = getOptionKey(option);
    // Check if any nested options are selected (keys that start with optionKey + '/')
    return Object.keys(selectedModifiers).some((key) => key.startsWith(`${optionKey}/`) && key !== optionKey);
  }, [selectedModifiers, getOptionKey]);

  const value = useMemo(
    () => ({
      isSelected,
      getTotalQty,
      handleChange,
      pushIntoStack,
      selectedModifiers,
      getSelectedOption,
      unSatisfiedModifiers,
      hasSelectionsSatisfied,
      loading: modifiers == null,
      getSelectedModifiersTree,
      getNormalizedSelectedModifiers,
      getModifier,
      modifiers,
      getNestedOptions,
      hasSelectedNestedOptions,
      setSelectedModifiers,
    }),
    [
      modifiers,
      isSelected,
      getTotalQty,
      handleChange,
      pushIntoStack,
      selectedModifiers,
      getSelectedOption,
      unSatisfiedModifiers,
      hasSelectionsSatisfied,
      getSelectedModifiersTree,
      getNormalizedSelectedModifiers,
      getModifier,
      getNestedOptions,
      hasSelectedNestedOptions,
    ],
  );

  return (
    <modifierContext.Provider value={value}>
      {children}
    </modifierContext.Provider>
  );
}

function getSelectedOptions({ allOptions, getModifier }) {
  const selectedOptions = allOptions.filter((o) => o.default_qty > 0);
  let revalidateRequired;
  do {
    revalidateRequired = false;
    selectedOptions.forEach((o) => {
      const modifier = getModifier(o);
      if (modifier.options_parents.length === 0) {
        return;
      }
      const parentOptions = allOptions.filter((op) => modifier.options_parents.includes(op.id));
      const parentOption = parentOptions.find((op) => op.default_qty > 0) || parentOptions[0];
      if (!selectedOptions.some((sp) => sp.id === parentOption.id)) {
        selectedOptions.push(parentOption);
        revalidateRequired = true;
      }
    });
  } while (revalidateRequired);
  return selectedOptions;
}

function initializeFromSelectedOptions({
  selectedOptionsProp, allOptions, getModifier, getOptionKey, modifiersTree,
}) {
  const restoredModifiers = {};

  // If modifiersTree is available, use it to restore the exact nested structure
  if (modifiersTree && Object.keys(modifiersTree).length > 0) {
    // modifiersTree has keys like "modifier-id-option-id/modifier-id-option-id" with { quantity }
    // The format is: "parentModifierId-parentOptionId/childModifierId-childOptionId"
    // Note: modifiersTree only contains leaf nodes (filtered to exclude parent keys that are prefixes)
    // So we need to restore all options in the path, including parents

    // First, collect all unique options that need to be restored
    const optionsToRestore = new Map(); // key -> { option, qty, isLeaf }

    Object.entries(modifiersTree).forEach(([key, { quantity }]) => {
      // Parse the key to get all option parts in the path
      const keyParts = key.split('/');
      // Each part is "modifier-id-option-id"
      // Restore all options in the path (both parent and child)
      keyParts.forEach((part, index) => {
        const [modifierId, optionId] = part.split('-').map(Number);
        // Find the option
        const option = allOptions.find((o) => o.id === optionId && o.modifier === modifierId);
        if (option) {
          // Build the key for this option (could be nested)
          const optionKey = keyParts.slice(0, index + 1).join('/');
          const isLeaf = index === keyParts.length - 1;

          // Store the option with its key
          // If it's a leaf, use the quantity from modifiersTree, otherwise use 1
          if (!optionsToRestore.has(optionKey)) {
            optionsToRestore.set(optionKey, {
              option,
              qty: isLeaf ? (quantity || 1) : 1,
              isLeaf,
            });
          } else if (isLeaf) {
            // Update quantity if this is the leaf node
            optionsToRestore.set(optionKey, {
              option,
              qty: quantity || 1,
              isLeaf: true,
            });
          }
        }
      });
    });

    // Now restore all options using the keys from modifiersTree
    // The keys in modifiersTree were generated by getSelectedModifiersTree which uses getOptionKey,
    // so they should match exactly. We'll use them directly to preserve the exact structure.
    // However, we need to restore in order (parents before children) so that getOptionKey works correctly
    // Sort by key length (shorter keys = parents, longer keys = children)
    const sortedOptions = Array.from(optionsToRestore.entries())
      .sort(([keyA], [keyB]) => keyA.split('/').length - keyB.split('/').length);

    sortedOptions.forEach(([storedKey, { option, qty }]) => {
      // Use the stored key from modifiersTree directly since it was generated by getSelectedModifiersTree
      // which uses getOptionKey. This ensures exact matching with the structure that was saved.
      // The stored key preserves the exact parent-child path structure.
      restoredModifiers[storedKey] = { ...option, qty };
    });

    return restoredModifiers;
  }

  // Fallback: reconstruct from selectedOptionsProp (for backward compatibility)
  // Find all selected option objects from allOptions
  const selectedOptionObjects = selectedOptionsProp
    .map((so) => {
      const option = allOptions.find((o) => o.id === so.id);
      if (!option) return null;
      return { ...option, qty: so.qty || 1 };
    })
    .filter(Boolean);

  // Add parent options for nested modifiers
  // When a nested modifier is selected, its parent must also be selected
  const allSelectedOptions = [...selectedOptionObjects];
  const selectedOptionIds = new Set(selectedOptionObjects.map((so) => so.id));
  let revalidateRequired = true;

  while (revalidateRequired) {
    revalidateRequired = false;
    allSelectedOptions.forEach((option) => {
      const modifier = getModifier(option);
      if (!modifier || modifier.options_parents.length === 0) {
        return;
      }
      // Find parent options that could be parents of this modifier
      const parentOptions = allOptions.filter((op) => modifier.options_parents.includes(op.id));
      // Check if we already have a parent option selected
      const hasParent = allSelectedOptions.some((so) => parentOptions.some((po) => po.id === so.id));

      if (!hasParent && parentOptions.length > 0) {
        // Try to find a parent that was in the original selectedOptionsProp
        // (in case it was filtered out because it wasn't found in allOptions)
        const parentFromOriginal = parentOptions.find((po) => selectedOptionIds.has(po.id));

        if (parentFromOriginal) {
          // Parent was in original selection, add it
          const parentOption = allOptions.find((o) => o.id === parentFromOriginal.id);
          if (parentOption && !allSelectedOptions.some((so) => so.id === parentOption.id)) {
            allSelectedOptions.push({ ...parentOption, qty: 1 });
            revalidateRequired = true;
          }
        } else if (parentOptions.length === 1) {
          // Only one possible parent, use it
          const parentOption = parentOptions[0];
          if (!allSelectedOptions.some((so) => so.id === parentOption.id)) {
            allSelectedOptions.push({ ...parentOption, qty: 1 });
            revalidateRequired = true;
          }
        }
        // If multiple possible parents and none in original selection, skip
        // (this shouldn't happen in normal flow, but handle gracefully)
      }
    });
  }

  // Build the modifiers object with proper keys
  allSelectedOptions.forEach((option) => {
    const key = getOptionKey(option);
    restoredModifiers[key] = option;
  });

  return restoredModifiers;
}
